import type { CreateFixedRateLoanProduct } from '@src/types/OTC';
import type { LoanSchedulePeriod } from '@src/types/OTC/loanSchedulePeriod';
import { isFixedRateLoanProductFields } from '@src/types/OTC/otcGuards';
import type { DealProductBuilderRow } from '@src/types/OTC/otcProductBuilderTypes';
import { isUnsecuredLoanSecurityType } from '@src/utils/OTC/loanSecurity';
import type { DayCountConventionEnum, OTCFixedRateLoanProductTemplate } from '@talos/kyoko';
import {
  LoanPaymentTypeEnum,
  LoanQuoteTerminationTypeEnum,
  LoanSecurityTypeEnum,
  logger,
  runValidationV1WithResult,
} from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import { resolveLoanScheduleDuration } from '../OTCFieldsDialog/loanPaymentType';
import { BaseProductDefinition, type CreateProductParams } from './BaseProductDefinition';
import { loanProductBuilderSchema, type ValidatedLoanRowData } from './FixedRateLoanProductBuilderSliceStrategy';
import { loanProductDetailsForCompare } from './loanProductDetailsForCompare';

export class FixedRateLoanProductDefinition extends BaseProductDefinition {
  private otcFixedRateLoanProductTemplatesByDisplaySymbol: Map<string, OTCFixedRateLoanProductTemplate> = new Map();

  updateReferenceData(otcFixedRateLoanProductTemplatesByDisplaySymbol: Map<string, OTCFixedRateLoanProductTemplate>) {
    this.otcFixedRateLoanProductTemplatesByDisplaySymbol = otcFixedRateLoanProductTemplatesByDisplaySymbol;
  }

  buildPostBody(row: ValidatedLoanRowData): CreateFixedRateLoanProduct | undefined {
    const validationRun = runValidationV1WithResult(loanProductBuilderSchema, row);
    if (validationRun.result === 'failure') {
      return;
    }

    const validatedData = validationRun.validatedData;
    const termType = validatedData.otcFields.termType as LoanQuoteTerminationTypeEnum;
    const loanPaymentType =
      (validatedData.otcFields.loanPaymentType as LoanPaymentTypeEnum | undefined) ?? LoanPaymentTypeEnum.Periodic;
    const scheduleDurationContext = {
      termType,
      term: validatedData.otcFields.term,
      initialTerm: validatedData.otcFields.initialTerm,
      loanPaymentType,
    };
    const paymentPeriod = resolveLoanScheduleDuration(
      validatedData.otcFields.paymentPeriod as LoanSchedulePeriod | undefined,
      scheduleDurationContext
    );
    const accrualPeriod = resolveLoanScheduleDuration(
      validatedData.otcFields.accrualPeriod as LoanSchedulePeriod | undefined,
      scheduleDurationContext
    );

    const loanSecurityType =
      (validatedData.loanSecurityType as LoanSecurityTypeEnum | undefined) ?? LoanSecurityTypeEnum.Secured;
    const isUnsecured = isUnsecuredLoanSecurityType(loanSecurityType);

    const result: CreateFixedRateLoanProduct = {
      ProductType: 'OTC',
      SubProductType: 'OTCFixedRateLoan',
      RequestType: 'OTC',
      Market: 'internal',
      ProductDetails: {
        TermType: termType,
        LoanPaymentType: loanPaymentType,
        UnitLoanAmount: validatedData.otcFields.unitLoanAmount,
        BorrowedAsset: validatedData.baseAsset,
        ...(isUnsecured
          ? { LoanSecurityType: LoanSecurityTypeEnum.Unsecured }
          : { CollateralAsset: validatedData.collateralAsset ?? undefined }),
        PaymentPeriod: paymentPeriod,
        AccrualPeriod: accrualPeriod,
        DayCountConvention: validatedData.otcFields.dayCountConvention as DayCountConventionEnum,
        LoanPaymentCcy: validatedData.otcFields.loanPaymentCcy,
        ...(termType === LoanQuoteTerminationTypeEnum.Fixed &&
          validatedData.otcFields.term && {
            Term: validatedData.otcFields.term,
          }),
        ...(termType === LoanQuoteTerminationTypeEnum.OpenTerm &&
          validatedData.otcFields.initialTerm && {
            InitialFixedTerm: validatedData.otcFields.initialTerm,
          }),
        ...(validatedData.uniqueSuffix && { UniqueSuffix: validatedData.uniqueSuffix }),
      },
    };
    return result;
  }

  getProductDetailsForComparison(row: DealProductBuilderRow): CreateFixedRateLoanProduct | null {
    if (!row.otcFields || !row.baseAsset || !isFixedRateLoanProductFields(row.otcFields)) {
      return null;
    }

    const validation = runValidationV1WithResult(loanProductBuilderSchema, row);
    if (validation.result === 'failure') {
      throw new Error('Product builder validation failed');
    }

    return this.buildPostBody(validation.validatedData) || null;
  }

  checkForUniqueness(
    rowData: DealProductBuilderRow & { product: 'fixedRateLoan' }
  ): 'notReady' | 'uncreated' | 'conflict' | 'alreadyCreated' {
    const validation = runValidationV1WithResult(loanProductBuilderSchema, rowData);
    if (validation.result === 'failure') {
      return 'notReady';
    }

    const productDetails = this.getProductDetailsForComparison(rowData);
    if (!productDetails) {
      return 'notReady';
    }

    const postBody = this.buildPostBody(validation.validatedData);
    if (!postBody) {
      return 'notReady';
    }
    const symbol =
      validation.validatedData.productTemplate +
      (postBody.ProductDetails.UniqueSuffix ? `-${postBody.ProductDetails.UniqueSuffix}` : '');

    const existingSecurity = this.otcFixedRateLoanProductTemplatesByDisplaySymbol.get(symbol);
    if (!existingSecurity) {
      return 'uncreated';
    }

    const existingProductDetails = existingSecurity?.ProductDetails;
    if (
      !isEqual(
        loanProductDetailsForCompare(postBody.ProductDetails),
        loanProductDetailsForCompare(existingProductDetails)
      )
    ) {
      return 'conflict';
    }

    return 'alreadyCreated';
  }

  async createProduct({
    rowData,
    createCustomInstrument,
    onSuccess,
    onError,
  }: CreateProductParams & {
    rowData: DealProductBuilderRow & { product: 'fixedRateLoan' };
  }): Promise<{ symbol: string } | undefined> {
    const validation = runValidationV1WithResult(loanProductBuilderSchema, rowData);
    if (validation.result === 'failure') {
      onError('Validation failed on OTC Loan');
      return;
    }

    try {
      this.checkForUniqueness(rowData);
      const validatedData = validation.validatedData;

      const loan = this.buildPostBody(validatedData);

      if (!loan) {
        onError('Failed to build loan product body');
        return;
      }

      const rows = await createCustomInstrument(loan);
      onSuccess('Loan product created successfully');
      logger.info('Loan product created successfully', { data: rows });

      return { symbol: rows[0]?.Symbol ?? '' };
    } catch (error) {
      if (error instanceof Error && error.name === 'ValidationError') {
        onError(`Validation failed: ${error.message}`);
      } else {
        const serverMessage = this.getServerErrorMessage(error);
        onError(serverMessage ? `Error creating loan product: ${serverMessage}` : 'Error creating loan product');
      }
      logger.error(new Error('Error creating loan product'), {
        extra: {
          input: { rowData },
          message: error instanceof Error ? error.message : 'Unknown error',
          error,
        },
      });
      return;
    }
  }
}
